import { useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useHasGate } from 'academy-common-lib/features/accounts/accountsQueries';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { EnlargeIcon } from 'foundations-assets/icons/components/EnlargeIcon';
import { RetractIcon } from 'foundations-assets/icons/components/RetractIcon';
import { SprocketIcon } from 'foundations-assets/icons/components/SprocketIcon';
import { UpIcon } from 'foundations-assets/icons/components/UpIcon';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { EditIcon } from 'foundations-assets/icons/components/EditIcon';
import { MessagesIcon } from 'foundations-assets/icons/components/MessagesIcon';
import { GradientCapsIcon } from './GradientCapsIcon';
import { useAssistantLayout, useEffectiveAssistantMode } from '../hooks/useAssistantLayout';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ChatHeader = ({
  isOnboardingFlow,
  isCompact,
  onClose,
  onNewChat,
  onOpenChats,
  trackEvent
}) => {
  const {
    isMobile: viewportMobile,
    isMobileNavbar: viewportMobileNavbar
  } = useMediaQuery();
  const isMobile = isCompact || viewportMobile;
  const isHistoryToggleable = isCompact || viewportMobileNavbar;
  const toggleMode = useAssistantLayout(state => state.toggleMode);
  const effectiveMode = useEffectiveAssistantMode();
  const {
    hasGate: isUngatedToCollapseToSidebar
  } = useHasGate('Academy:AiAssistant:AskAcademyAssistant');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  if (isOnboardingFlow) {
    return /*#__PURE__*/_jsx(View, {
      paddingInline: "300",
      paddingBlockStart: isMobile ? '300' : '500',
      paddingBlockEnd: "300",
      children: /*#__PURE__*/_jsx(SprocketIcon, {
        color: "fill-brand-default",
        size: "800"
      })
    });
  }
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    justifyContent: "space-between",
    inlineSize: "100%",
    blockSize: "56px",
    paddingBlock: "400",
    children: [/*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      gap: "200",
      flex: 1,
      justifyContent: "start",
      children: /*#__PURE__*/_jsx(Dropdown, {
        open: isMenuOpen,
        onOpenChange: e => setIsMenuOpen(e.target.value),
        placement: "bottom left",
        buttonSize: "sm",
        icon: () => /*#__PURE__*/_jsx(View, {
          as: "span",
          marginInlineEnd: "200",
          children: /*#__PURE__*/_jsx(GradientCapsIcon, {
            size: 20
          })
        }),
        buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academy-ai-assistant.chat.title"
        }),
        caretRenderer: () => isMenuOpen ? /*#__PURE__*/_jsx(UpIcon, {
          size: "100",
          color: "text-core-default"
        }) : /*#__PURE__*/_jsx(DownIcon, {
          size: "100",
          color: "text-core-default"
        }),
        buttonStyles: {
          borderRadius: 'full',
          borderWidth: '0'
        },
        children: /*#__PURE__*/_jsxs(List, {
          children: [/*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(Button, {
              use: "unstyled",
              onClick: () => {
                trackEvent('new-chat-started');
                onNewChat();
              },
              children: /*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                gap: "200",
                paddingInline: "300",
                paddingBlock: "200",
                children: [/*#__PURE__*/_jsx(EditIcon, {
                  size: "100",
                  color: "text-core-default"
                }), /*#__PURE__*/_jsx(Text, {
                  color: "text-core-default",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "academy-ai-assistant.chat.newChat"
                  })
                })]
              })
            })
          }), isHistoryToggleable && /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(Button, {
              use: "unstyled",
              onClick: () => {
                trackEvent('history-drawer-opened');
                onOpenChats();
              },
              children: /*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                gap: "200",
                paddingInline: "300",
                paddingBlock: "200",
                children: [/*#__PURE__*/_jsx(MessagesIcon, {
                  size: "100",
                  color: "text-core-default"
                }), /*#__PURE__*/_jsx(Text, {
                  color: "text-core-default",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "academy-ai-assistant.drawer.title"
                  })
                })]
              })
            })
          })]
        })
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "400",
      children: [!viewportMobile && isUngatedToCollapseToSidebar && /*#__PURE__*/_jsx(Tooltip, {
        placement: "bottom left",
        title: effectiveMode === 'sidebar' ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academy-ai-assistant.chat.expandToFullscreen"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academy-ai-assistant.chat.collapseToSidebar"
        }),
        children: /*#__PURE__*/_jsx(IconButton, {
          "aria-label": effectiveMode === 'sidebar' ? I18n.text('academy-ai-assistant.chat.expandToFullscreen') : I18n.text('academy-ai-assistant.chat.collapseToSidebar'),
          onClick: () => toggleMode(),
          shape: "circle",
          use: "transparent",
          size: "sm",
          children: effectiveMode === 'sidebar' ? /*#__PURE__*/_jsx(EnlargeIcon, {
            size: "200"
          }) : /*#__PURE__*/_jsx(RetractIcon, {
            size: "200"
          })
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        inline: true,
        size: "sm",
        "aria-label": I18n.text('academy-ai-assistant.chat.close'),
        onClick: () => {
          trackEvent('chat-closed');
          onClose();
        }
      })]
    })]
  });
};